// Canonical schedule status-bucket vocabulary. Pure helper — no React.
// The three pages that touch status buckets (StatsRow tile counts,
// FilterStrip cycle/labels, Schedule.tsx URL filtering) all import from
// here so the bucket spelling, labels, and predicates can never drift.
//
// Slug rationale (locked):
//   overdue            — red severity (any kind)
//   due-soon           — brand severity inside a 7-CALENDAR-DAY window
//                        from today (the chip reads "Due this week"; the
//                        backend's normaliser "due-soon" is 14 days —
//                        these share a name but mean different things,
//                        and that's load-bearing for the chip label)
//   pending-decisions  — amber severity, kind ∈ {variation, deliverable}
//
// The previous draft used two different vocabularies (FilterStrip
// emitted "due-this-week"/"pending-decisions"; Schedule emitted
// "due-soon"/"pending-decision"); the type guard normalises any
// historical or pasted URL value to undefined so a stale link never
// silently activates a bucket.

import type { Activity } from "../../../types/schedule";

export type ScheduleStatusBucket = "overdue" | "due-soon" | "pending-decisions";

const BUCKETS: readonly ScheduleStatusBucket[] = [
  "overdue",
  "due-soon",
  "pending-decisions",
] as const;

export function isScheduleStatusBucket(
  value: unknown,
): value is ScheduleStatusBucket {
  return (
    typeof value === "string" && (BUCKETS as readonly string[]).includes(value)
  );
}

export const SCHEDULE_BUCKET_LABEL: Record<ScheduleStatusBucket, string> = {
  overdue: "Overdue only",
  "due-soon": "Due this week only",
  "pending-decisions": "Pending decisions only",
};

export const SCHEDULE_BUCKET_CYCLE: ReadonlyArray<
  ScheduleStatusBucket | undefined
> = [undefined, "overdue", "due-soon", "pending-decisions"];

const DAY_MS = 86_400_000;

// Predicates — locked verbatim. "due-soon" here is a 7-CALENDAR-DAY
// window from today (UTC), matching the chip's "Due this week" label;
// the backend's normaliser also uses the name "due-soon" but with a
// 14-day condition (DUE_SOON_DAYS in normaliseActivity.ts) — they share
// a name but mean different things. Both readings are intentional.
export function matchesBucket(
  a: Activity,
  bucket: ScheduleStatusBucket | undefined,
  today: Date,
): boolean {
  if (!bucket) return true;
  if (bucket === "overdue") return a.status === "red";
  const todayMs = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  if (bucket === "due-soon") {
    if (a.status !== "brand") return false;
    const d = new Date(`${a.date}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return false;
    const dm = Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate(),
    );
    return dm >= todayMs && dm <= todayMs + 7 * DAY_MS;
  }
  // pending-decisions — amber + deliverable only. (Variations were
  // removed from the schedule vocabulary; see design doc §8.)
  return a.status === "amber" && a.kind === "deliverable";
}

export function filterByBucket(
  activities: Activity[],
  bucket: ScheduleStatusBucket | undefined,
  today: Date,
): Activity[] {
  if (!bucket) return activities;
  return activities.filter((a) => matchesBucket(a, bucket, today));
}
